import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Async Thunks
// export const fetchProjects = createAsyncThunk(
//   'projects/fetchProjects',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await api.get('/projects');
//       return response.data.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || 'Failed to fetch projects');
//     }
//   }
// );

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/projects', { params });
      return response.data; // Ensure the backend returns { data, pagination }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch projects');
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/projects', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create project');
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchProjectById',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      return response.data.data; // assuming your API returns the project details
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch project');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      formData.append('_method', 'PATCH'); // Laravel compatibility for update
      const response = await api.post(`/projects/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Delete failed');
    }
  }
);

// Fetch eligible translators for a given project
export const fetchEligibleTranslators = createAsyncThunk(
  'projects/fetchEligibleTranslators',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/eligible-translators`);
      return { projectId, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch eligible translators');
    }
  }
);

export const fetchAssignments = createAsyncThunk(
  'projects/fetchAssignments',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/assignments`);
      // response.data.data should be an array of assignments.
      return { projectId, assignments: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch assignments');
    }
  }
);

// Updated thunk to handle bulk creation
export const createAssignment = createAsyncThunk(
  'projects/createAssignment',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      // Expect the backend to return an array of created assignments
      const response = await api.post(`/projects/${projectId}/assignments`, data);
      return { projectId, assignments: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create assignment');
    }
  }
);

export const updateAssignment = createAsyncThunk(
  'projects/updateAssignment',
  async ({ projectId, assignmentId, data }, { rejectWithValue }) => {
    try {
      // Use PATCH for partial updates.
      const response = await api.patch(`/projects/${projectId}/assignments/${assignmentId}`, data);
      return { projectId, assignment: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update assignment');
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  'projects/deleteAssignment',
  async ({ projectId, assignmentId }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/assignments/${assignmentId}`);
      return { projectId, assignmentId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete assignment');
    }
  }
);


// In your projectSlice (or wherever sendBid is defined)
export const sendBid = createAsyncThunk(
  "projects/sendBid",
  async ({ projectId, translatorId, services, note }, { rejectWithValue }) => {
    try {
      // Pass the data as { services: [...] } to satisfy backend validation.
      const response = await api.post(
        `/projects/${projectId}/bids/send/${translatorId}`,
        { services, note }
      );
      return { projectId, translatorId, services, note, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to send bid");
    }
  }
);

// New Thunks for Bid CRUD

export const createBid = createAsyncThunk(
  'projects/createBid',
  async ({ projectId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/bids`, formData);
      return { projectId, bid: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create bid');
    }
  }
);

export const updateBid = createAsyncThunk(
  'projects/updateBid',
  async ({ projectId, bidId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}/bids/${bidId}`, formData);
      return { projectId, bid: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update bid');
    }
  }
);

export const deleteBid = createAsyncThunk(
  'projects/deleteBid',
  async ({ projectId, bidId }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/bids/${bidId}`);
      return { projectId, bidId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete bid');
    }
  }
);

export const fetchProjectBids = createAsyncThunk(
  "projects/fetchProjectBids",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/bids`);
      return { projectId, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch bids');
    }
  }
);

export const fetchQuotations = createAsyncThunk(
  'projects/fetchQuotations',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/quotations`);
      return { projectId, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch quotations');
    }
  }
);

export const createQuotation = createAsyncThunk(
  'projects/createQuotation',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/quotations`, data);
      return { projectId, quotation: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create quotation');
    }
  }
);

export const updateQuotation = createAsyncThunk(
  'projects/updateQuotation',
  async ({ projectId, quotationId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}/quotations/${quotationId}`, data);
      return { projectId, quotation: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update quotation');
    }
  }
);

export const deleteQuotation = createAsyncThunk(
  'projects/deleteQuotation',
  async ({ projectId, quotationId }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/quotations/${quotationId}`);
      return { projectId, quotationId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete quotation');
    }
  }
);

export const sendQuotationEmail = createAsyncThunk(
  'projects/sendQuotationEmail',
  async ({ projectId, quotationId, clientEmail }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/quotations/${quotationId}/send`, {
        client_email: clientEmail, // Pass the client's email
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to send quotation email');
    }
  }
);


// Assign translators for a given project
export const assignTranslators = createAsyncThunk(
  'projects/assignTranslators',
  async ({ projectId, assignments }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/assign-translators`, {
        assignments,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to assign translators');
    }
  }
);

export const resendUpdatedBidEmail = createAsyncThunk(
  'projects/resendUpdatedBidEmail',
  async ({ projectId, bidId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/bids/${bidId}/resend-updated`);
      return { projectId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to resend updated bid email');
    }
  }
);

// Slice
const projectSlice = createSlice({
  name: 'projects',
  initialState: {
    projects: [],
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
    },
    loading: false,
    error: null,
    assignments: {},
    assignmentsLoading: false,
    bids: {},
    bidsLoading: false,
    quotations: {},
    quotationsLoading: false,
    eligibleTranslators: {}, // Store eligible translators by project ID
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle fetchProjectById
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        const fetchedProject = action.payload;
        const index = state.projects.findIndex((p) => p.id === fetchedProject.id);
        if (index === -1) {
          state.projects.push(fetchedProject);
        } else {
          state.projects[index] = fetchedProject;
        }
      })

      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Project
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Project
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter((p) => p.id !== action.payload);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Eligible Translators
      .addCase(fetchEligibleTranslators.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchEligibleTranslators.fulfilled, (state, action) => {
        state.eligibleTranslators[action.payload.projectId] = action.payload.data;
      })
      .addCase(fetchEligibleTranslators.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(sendBid.fulfilled, (state, action) => {
        console.log("Bid sent:", action.payload);
      })

      // Fetch Project Bids
      .addCase(fetchProjectBids.pending, (state) => {
        state.bidsLoading = true;
      })
      .addCase(fetchProjectBids.fulfilled, (state, action) => {
        state.bidsLoading = false;
        state.bids[action.payload.projectId] = action.payload.data;
      })
      .addCase(fetchProjectBids.rejected, (state, action) => {
        state.bidsLoading = false;
        state.error = action.payload;
      })

      // Create Bid
      .addCase(createBid.pending, (state) => {
        state.error = null;
      })
      .addCase(createBid.fulfilled, (state, action) => {
        const { projectId, bid } = action.payload;
        if (!state.bids[projectId]) state.bids[projectId] = [];
        state.bids[projectId].push(bid);
      })
      .addCase(createBid.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update Bid
      .addCase(updateBid.pending, (state) => {
        state.error = null;
      })
      .addCase(updateBid.fulfilled, (state, action) => {
        const { projectId, bid } = action.payload;
        if (state.bids[projectId]) {
          const index = state.bids[projectId].findIndex((b) => b.id === bid.id);
          if (index !== -1) state.bids[projectId][index] = bid;
        }
      })
      .addCase(updateBid.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete Bid
      .addCase(deleteBid.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteBid.fulfilled, (state, action) => {
        const { projectId, bidId } = action.payload;
        if (state.bids[projectId]) {
          state.bids[projectId] = state.bids[projectId].filter((b) => b.id !== bidId);
        }
      })
      .addCase(deleteBid.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchQuotations.pending, (state) => {
        state.quotationsLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotations.fulfilled, (state, action) => {
        state.quotationsLoading = false;
        state.quotations[action.payload.projectId] = action.payload.data;
      })
      .addCase(fetchQuotations.rejected, (state, action) => {
        state.quotationsLoading = false;
        state.error = action.payload;
      })

      // Create Quotation
      .addCase(createQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.quotations[action.payload.projectId]) {
          state.quotations[action.payload.projectId] = [];
        }
        state.quotations[action.payload.projectId].push(action.payload.quotation);
      })
      .addCase(createQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Quotation
      .addCase(updateQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const quotations = state.quotations[action.payload.projectId];
        if (quotations) {
          const index = quotations.findIndex((q) => q.id === action.payload.quotation.id);
          if (index !== -1) {
            quotations[index] = action.payload.quotation;
          }
        }
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Quotation
      .addCase(deleteQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const quotations = state.quotations[action.payload.projectId];
        if (quotations) {
          state.quotations[action.payload.projectId] = quotations.filter(
            (q) => q.id !== action.payload.quotationId
          );
        }
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send Quotation Email
      .addCase(sendQuotationEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendQuotationEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendQuotationEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Assign Translators
      .addCase(assignTranslators.pending, (state) => {
        state.error = null;
      })
      .addCase(assignTranslators.fulfilled, (state, action) => {
        const index = state.projects.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
      })
      .addCase(assignTranslators.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignmentsLoading = false;
        const { projectId, assignments } = action.payload;
        state.assignments[projectId] = assignments;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.assignmentsLoading = false;
        state.error = action.payload;
      })

      // ------------- CREATE ASSIGNMENT (Bulk) -------------
      .addCase(createAssignment.pending, (state) => {
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        const { projectId, assignments } = action.payload;
        // If assignments for this project do not exist, initialize as an empty array.
        if (!state.assignments[projectId]) state.assignments[projectId] = [];
        // Concatenate the new assignments to the existing ones.
        state.assignments[projectId] = state.assignments[projectId].concat(assignments);
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ------------- UPDATE ASSIGNMENT -------------
      .addCase(updateAssignment.pending, (state) => {
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        const { projectId, assignment } = action.payload;
        if (state.assignments[projectId]) {
          const index = state.assignments[projectId].findIndex(a => a.id === assignment.id);
          if (index !== -1) {
            state.assignments[projectId][index] = assignment;
          }
        }
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ------------- DELETE ASSIGNMENT -------------
      .addCase(deleteAssignment.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        const { projectId, assignmentId } = action.payload;
        if (state.assignments[projectId]) {
          state.assignments[projectId] = state.assignments[projectId].filter(a => a.id !== assignmentId);
        }
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(resendUpdatedBidEmail.pending, (state) => {
        // Optionally, set a loading state for resending email.
      })
      .addCase(resendUpdatedBidEmail.fulfilled, (state, action) => {
        console.log("Resent bid email:", action.payload);
      })
      .addCase(resendUpdatedBidEmail.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default projectSlice.reducer;